import React from 'react'
import { Modal } from 'antd'
import { Button } from '@surecompanies/core_components'
import isNil from 'lodash/isNil'
import {
  ModalSubtitle,
  SyncEmptyState,
} from './ClientBillingProfileManagement.styles'
import { AccessResultList } from '../UserManagement/AccessResultList'
import { failedEmails, GRANT_OUTCOMES } from '../UserManagement/grantReport'
import type { useAccessGrantRun } from './useAccessGrantRun'

interface AccessGrantResultsModalProps {
  state: ReturnType<typeof useAccessGrantRun>
}

/**
 * What granting access did to each address. A ticked address is invited, sent a
 * new invitation, or simply given the role depending on the account behind it,
 * so the outcome is only knowable per recipient once the run lands.
 */
const AccessGrantResultsModal = ({ state }: AccessGrantResultsModalProps) => {
  const retryable = failedEmails(state.results)

  return (
    <Modal
      open={state.isOpen}
      title="Access results"
      onCancel={state.handleClose}
      width={560}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
      // The report only exists while this modal is open, so leaving mid-run
      // would hide which addresses failed.
      closable={!state.isRunning}
      maskClosable={!state.isRunning}
      keyboard={!state.isRunning}
      footer={[
        retryable.length > 0 ? (
          <Button
            key="retry"
            loading={state.isRunning}
            onClick={state.handleRetryFailed}
          >
            Retry failed ({retryable.length})
          </Button>
        ) : null,
        <Button
          key="done"
          type="primary"
          disabled={state.isRunning}
          onClick={state.handleClose}
        >
          Done
        </Button>,
      ]}
    >
      <ModalSubtitle>{state.context}</ModalSubtitle>

      {isNil(state.results) ? (
        <SyncEmptyState aria-live="polite">
          {isNil(state.progress)
            ? 'Granting access…'
            : `Processed ${state.progress.processed} of ${state.progress.total}…`}
        </SyncEmptyState>
      ) : (
        <AccessResultList
          items={state.results}
          outcomes={GRANT_OUTCOMES}
          companiesByEmail={state.companiesByEmail}
          progress={state.progress}
          progressLabel="Retrying"
        />
      )}
    </Modal>
  )
}

export { AccessGrantResultsModal }
